import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBrandIdentity } from '../utils/brandIdentity';
import PageHeading from '../components/PageHeading';
import { IconCopy, IconLock, IconRefresh, IconSearch, IconX } from '../components/Icons';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { useAuth } from '../contexts/AuthContext';
import CsaDirectory from '../components/tradeWatcher/CsaDirectory';
import ClientFeedLookup from '../components/tradeWatcher/ClientFeedLookup';
import TradeWatcherConnections from '../components/tradeWatcher/TradeWatcherConnections';
import { TallyTwoDownloads, TallyTwoReleases } from '../components/tradeWatcher/TallyTwoAdmin';
import usePersistentTab from '../hooks/usePersistentTab';
import './VectorDashboardPage.css';
import './TradeWatcher.css';
import '../components/BulkRiskCalculator.css';
import '../components/DashboardProLock.css';

// Full Admin's Alerts tab (unprotected positions); loaded only when opened.
const TradeWatchAlerts = React.lazy(() => import('../components/tradeWatcher/TradeWatchAlerts'));

/**
 * "Preview as" tier while a Platform Admin previews a lower tier. Sent on every
 * Trade Watcher request so the server returns that tier's board (e.g. Pro sees
 * only their own accounts). Set by the page component on each render.
 */
let previewViewAs = null;

/** The company's app name (RiskLo for the RiskLo home brand). */
function appName() {
  return getBrandIdentity().appName;
}

function twHeaders() {
  const headers = getBearerAuthHeaders();
  return previewViewAs ? { ...headers, 'X-RiskLo-Admin-View-As': previewViewAs } : headers;
}

const REFRESH_MS = 15000;
const LAYOUT_KEY = 'riskloTradeWatcherLayout';
const GROUP_KEY = 'riskloTradeWatcherGroup';
const HIDE_DAYS_KEY = 'riskloTradeWatcherHideOfflineDays';
const DEFAULT_HIDE_DAYS = 7;
const VIEW_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'problems', label: 'Problems' },
  { id: 'online', label: 'Connected' },
  { id: 'offline', label: 'Offline' },
  { id: 'naked', label: 'Naked' },
  { id: 'trading', label: 'Trading' },
];
// Connected, Offline, Naked and Trading are picked by clicking the tiles above.
const VIEW_BUTTONS = ['all', 'problems'];
const BUILD_FILTERS = [
  { id: '', label: 'All' },
  { id: 'self', label: 'Self-use' },
  { id: 'hands', label: 'Hands-off' },
];
const CSA_COLORS = ['#38bdf8', '#34d399', '#f472b6', '#fbbf24', '#a78bfa', '#fb923c', '#2dd4bf', '#f87171'];

function readLayout() {
  try { return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'cards'; } catch (_e) { return 'cards'; }
}

function readGroup() {
  try { return localStorage.getItem(GROUP_KEY) === 'client' ? 'client' : 'account'; } catch (_e) { return 'account'; }
}

function sumOf(rows, key) {
  return rows.reduce((total, r) => total + (Number(r[key]) || 0), 0);
}

/** A client is one trader: their feed plus the name they send (two "John"s are two clients). */
function clientIdOf(row) {
  return row?.clientId || `${row?.feedId}:${String(row?.client || '').trim().toLowerCase()}`;
}

/** Rows → one group per client, problems first, then largest, then by name. */
function groupByClient(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = clientIdOf(row);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return [...map.values()].map((accounts) => {
    const csaCounts = new Map();
    for (const a of accounts) if (a.csa) csaCounts.set(a.csa, (csaCounts.get(a.csa) || 0) + 1);
    const csa = [...csaCounts.entries()].sort((x, y) => y[1] - x[1])[0]?.[0] || null;
    const lastSeen = accounts.map((a) => a.lastSeen).filter(Boolean).sort().pop() || null;
    return {
      key: clientIdOf(accounts[0]),
      client: accounts[0].client,
      clientEmail: accounts[0].clientEmail || null,
      csa,
      accounts,
      connected: accounts.filter((a) => a.connected === 'Yes').length,
      offline: accounts.filter((a) => a.connected === 'No').length,
      naked: accounts.filter((a) => a.naked === 'Yes').length,
      trading: accounts.filter((a) => a.trading === 'Yes').length,
      open: accounts.filter((a) => tradeStateOf(a) === 'open').length,
      runningPnl: sumOf(accounts, 'runningPnl'),
      dayPnl: sumOf(accounts, 'dayPnl'),
      openPnl: sumOf(accounts, 'openPnl'),
      accumulatedPnl: sumOf(accounts, 'accumulatedPnl'),
      lastSeen,
      problem: accounts.some(isProblem),
    };
  }).sort((a, b) => Number(b.problem) - Number(a.problem)
    || b.accounts.length - a.accounts.length
    || String(a.client).localeCompare(String(b.client)));
}

/** Days offline before an account is hidden; 0 = never hide. Per-viewer preference. */
function readHideDays() {
  try {
    const raw = localStorage.getItem(HIDE_DAYS_KEY);
    if (raw === null) return DEFAULT_HIDE_DAYS;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : DEFAULT_HIDE_DAYS;
  } catch (_e) {
    return DEFAULT_HIDE_DAYS;
  }
}

function isLongOffline(row, hideDays) {
  return hideDays > 0 && row.connected === 'No' && Number(row.offlineDays) >= hideDays;
}

function offlineLabel(row) {
  const since = row.offlineSince || row.disconnectedSince;
  if (!since) return 'Offline';
  const days = Number(row.offlineDays) || 0;
  return days >= 1 ? `Offline ${days}d · since ${fmtTime(since)}` : `Offline since ${fmtTime(since)}`;
}

function money(value) {
  if (value === null || value === undefined) return '$0.00';
  const n = Number(value);
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}$${abs}`;
}

function pnlClass(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '';
  return n > 0 ? 'is-up' : 'is-down';
}

function fmtTime(iso) {
  if (!iso) return '—';
  // Always Eastern, whatever the viewer's timezone (Vector's request).
  const text = new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  return `${text} ET`;
}

function agoText(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return fmtTime(iso);
}

function csaColor(name) {
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CSA_COLORS[h % CSA_COLORS.length];
}

function rowKey(row) {
  return `${row.feedId}:${row.ntAccount}`;
}

function isProblem(row) {
  return row.connected === 'No' || row.naked === 'Yes' || row.clockOk === 'No' || Boolean(row.buildCheck);
}

/** Support view: a client with a CSA must run the alert-only build ('wrong' / 'unknown'). */
function worstBuildCheck(rows) {
  if (rows.some((r) => r.buildCheck === 'wrong')) return 'wrong';
  return rows.some((r) => r.buildCheck === 'unknown') ? 'unknown' : null;
}

const BUILD_CHECK_TIP = {
  wrong: 'This client has a CSA, but the computer runs the self-use build, which can close trades. Reinstall with the alert-only build.',
  unknown: 'This client has a CSA, but the computer doesn’t say which build it runs (an older Tally or the classic add-on may auto-close). Reinstall with the alert-only build.',
};

function BuildCheckChip({ check }) {
  if (!check) return null;
  return (
    <span className={`tw-chip is-${check === 'wrong' ? 'bad' : 'warn'}`} title={BUILD_CHECK_TIP[check]}>
      <span aria-hidden="true">⚠ </span>{check === 'wrong' ? 'Wrong build' : 'Unknown build'}
    </span>
  );
}

/** "Trading today" split: open position right now vs traded and flat. */
function tradeStateOf(row) {
  if (row.trading !== 'Yes') return null;
  if (row.openPosition === true) return 'open';
  if (row.openPosition === false) return 'closed';
  return null;
}

function clientKey(name) {
  return String(name || '').trim().toLowerCase();
}

function matches(row, { q, view, tradeState = 'all' }) {
  if (q) {
    const hay = `${row.client} ${row.ntAccount} ${row.csa || ''} ${row.clientEmail || ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (view === 'problems' && !isProblem(row)) return false;
  if (view === 'online' && row.connected !== 'Yes') return false;
  if (view === 'offline' && row.connected !== 'No') return false;
  if (view === 'naked' && row.naked !== 'Yes') return false;
  if (view === 'trading' && row.trading !== 'Yes') return false;
  if (view === 'trading' && tradeState !== 'all' && tradeStateOf(row) !== tradeState) return false;
  return true;
}

function initials(name) {
  return String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

function StatusChip({ tone, children, title }) {
  return <span className={`tw-chip is-${tone}`} title={title}>{children}</span>;
}

function StatusChips({ row }) {
  return (
    <div className="tw-chips">
      <StatusChip tone={row.connected === 'Yes' ? 'good' : 'bad'}>{row.connected === 'Yes' ? 'Connected' : 'Disconnected'}</StatusChip>
      {row.stopsChecked === false
        ? <StatusChip tone="muted" title="This broker's stop orders aren't checked.">Stops not checked</StatusChip>
        : <StatusChip tone={row.naked === 'Yes' ? 'bad' : 'good'}>{row.naked === 'Yes' ? 'Naked' : 'Protected'}</StatusChip>}
      {(() => {
        const state = tradeStateOf(row);
        if (state === 'open') return <StatusChip tone="accent">Open position</StatusChip>;
        if (state === 'closed') return <StatusChip tone="muted">Closed today</StatusChip>;
        return <StatusChip tone={row.trading === 'Yes' ? 'accent' : 'muted'}>{row.trading === 'Yes' ? 'Trading' : 'No trades'}</StatusChip>;
      })()}
      {row.autoClosedAt ? (
        <span title="The add-on's auto-close closed a naked position on this account today. See Logs for details.">
          <StatusChip tone="warn">{`Auto-closed ${new Date(row.autoClosedAt).toLocaleTimeString('en-US', {
            timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
          })} ET`}</StatusChip>
        </span>
      ) : null}
      {row.clockOk === 'No' ? <StatusChip tone="bad" title={`NinjaTrader's time zone is ${row.clockTz || 'not Eastern'}. Set Tools → Options → General → Time zone to Eastern Time.`}>Time zone off</StatusChip> : null}
      <BuildCheckChip check={row.buildCheck} />
    </div>
  );
}

/**
 * Support view: computers of clients with a CSA that aren't on the alert-only
 * build. Clears by itself once the computer reports the alert-only build.
 */
function BuildMismatchBanner({ items }) {
  if (!items || items.length === 0) return null;
  const wrong = items.some((m) => m.kind === 'wrong');
  return (
    <section className={`tw-build-alert is-${wrong ? 'wrong' : 'unknown'}`} role="alert">
      <div className="tw-build-alert-head">
        <span aria-hidden="true">⚠</span>
        <strong>
          {wrong ? 'Wrong add-on build' : 'Unknown add-on build'} · {items.length} computer{items.length === 1 ? '' : 's'}
        </strong>
      </div>
      <p>
        These clients have a CSA, so their computers must run the alert-only build, which can’t touch trades.
        {wrong ? ' The self-use build can close trades.' : ''} Reinstall with the alert-only build; this clears on its own once it reports in.
      </p>
      <ul className="tw-build-alert-list">
        {items.map((m) => (
          <li key={`${m.feedId}|${m.client}|${m.machine || ''}`}>
            <div>
              <span className="tw-build-alert-client">{m.client}</span>
              <span className="tw-muted">CSA: {m.csa}</span>
            </div>
            <div>
              <span className="tw-muted">Computer</span>
              <span>{m.machine || 'Not reported'}</span>
            </div>
            <div>
              <span className="tw-muted">{m.kind === 'wrong' ? `Build: ${m.build}` : 'Build not reported'}</span>
              <span>
                {m.accounts.length} account{m.accounts.length === 1 ? '' : 's'} · seen {agoText(m.lastSeen)}
              </span>
            </div>
            <a className="vd-btn vd-client-resync-btn tw-build-alert-dl" href={`${API_ENDPOINTS.tradeWatcherAddonDownload}?package=csa`} download>
              Alert-only build
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Support view (Support and up): CSA tags, client and CSA filters, grouping by client. */
const SupportViewContext = createContext(false);

/** Support view: whose accounts these are, so two traders with the same name can be told apart. */
function ClientEmail({ email }) {
  const supportView = useContext(SupportViewContext);
  if (!supportView || !email) return null;
  return <div className="tw-client-email" title={email}>{email}</div>;
}

function CsaTag({ csa }) {
  const supportView = useContext(SupportViewContext);
  if (!supportView) return null;
  if (!csa) return <span className="tw-csa is-empty">Unassigned</span>;
  const color = csaColor(csa);
  return <span className="tw-csa" style={{ color, borderColor: `${color}55`, background: `${color}1a` }}>{csa}</span>;
}

/**
 * Support view: which RiskLo Tally build the account's computer runs. Self-use =
 * the trader installs and runs it (auto-close possible); Hands-off = the CSA
 * alert-only build (never closes a trade); Unknown = an add-on that doesn't say.
 */
const BUILD_BADGES = {
  self: { label: 'Self-use', tip: 'Self-use build: the trader runs it themselves; Tally can auto-close if they switch it on.' },
  hands: { label: 'Hands-off', tip: 'CSA alert-only build: alerts only, it can never close a trade.' },
  unknown: { label: 'Unknown build', tip: 'This computer’s add-on doesn’t report its build (an older Tally or the classic add-on).' },
};

function buildKind(row) {
  const b = String(row?.build || '').trim().toLowerCase();
  if (b === 'csa-alertonly') return 'hands';
  if (b === 'self-use') return 'self';
  return 'unknown';
}

/**
 * Broker accounts (Alpaca, Tradovate, SnapTrade brokerages, Coinbase), switched
 * on under Connections: RiskLo reads them; they have no add-on, logs or auto-close.
 */
function isBroker(row) {
  return row?.source === 'broker';
}

/** Brokers that don't give yesterday's close: no day/running P/L, account value instead. */
function hasTodayPnl(row) {
  return !isBroker(row) || row.pnlBasis !== 'unrealized';
}

/** "—" for a broker figure the broker doesn't provide, money otherwise. */
function figure(row, value) {
  return isBroker(row) && (value === null || value === undefined) ? '—' : money(value);
}

function BrokerTag({ row }) {
  if (!isBroker(row)) return null;
  return (
    <span className="tw-broker-tag" title={`${row.brokerName} account. RiskLo reads it about once a minute while the board is open. Manage it under Connections.`}>
      {row.brokerName}
    </span>
  );
}

function LogsButton({ row, onLogs }) {
  if (isBroker(row)) return null;
  return <button type="button" className="tw-text-btn" onClick={() => onLogs(row)}>Logs</button>;
}

function BuildBadge({ row }) {
  const supportView = useContext(SupportViewContext);
  if (!supportView || isBroker(row)) return null;
  const kind = buildKind(row);
  return <span className={`tw-build-badge is-${kind}`} title={BUILD_BADGES[kind].tip}>{BUILD_BADGES[kind].label}</span>;
}

/* ------------------------------------------------------------------ */
/* Board actions (auto-close switches)                                 */
/* ------------------------------------------------------------------ */

const BoardActionsContext = createContext(null);

/** The CSA alert-only build: alerts only, it has no code that can close a trade. */
function isCsaBuild(row) {
  return String(row?.build || '').trim().toLowerCase() === 'csa-alertonly';
}

/* ------------------------------------------------------------------ */
/* Auto-close (Tally self-use build: closes a naked position itself)   */
/* ------------------------------------------------------------------ */

/**
 * Only RiskLo Tally's self-use build reports auto-close, and only the account's
 * own trader may switch it (the server decides: canSwitchAutoClose).
 */
function canAutoClose(row) {
  return typeof row?.autoClose === 'boolean' && !isCsaBuild(row) && row.canSwitchAutoClose === true;
}

/** What the switch shows: a change still on its way to NinjaTrader wins. */
function autoCloseShownOn(row) {
  return row.autoClosePending ? row.autoClosePending === 'on' : row.autoClose === true;
}

const AUTO_CLOSE_EXPLAINED = 'Auto-close is the add-on’s safety net in NinjaTrader. If a position sits without a protective '
  + 'stop for the grace time set in the add-on (10 seconds unless changed), it sends the Naked alert and then closes '
  + 'that position at market. Off = you only get the alert and the position stays open. Only you can switch it, and only '
  + 'on your own self-use accounts.';

/** Hover or focus pop-up explaining a control. */
function InfoTip({ text, align = 'center', placement = 'down', children }) {
  return (
    <span className={`tw-tip-wrap is-${align} is-${placement}`}>
      {children}
      <span className="tw-tip" role="tooltip">{text}</span>
    </span>
  );
}

function AutoCloseSwitch({ row }) {
  const ctx = useContext(BoardActionsContext);
  if (!ctx || !canAutoClose(row)) return null;
  const pending = row.autoClosePending;
  const on = autoCloseShownOn(row);
  const flip = () => {
    if (pending) return;
    if (on && !window.confirm(`Turn auto-close off for ${row.ntAccount}?\n\nA naked position there will only raise an alert and stay open.`)) return;
    ctx.autoClose([row], !on);
  };
  return (
    <InfoTip placement="up" align="right" text={`${AUTO_CLOSE_EXPLAINED} This switch is for ${row.ntAccount} only.`}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`Auto-close for ${row.ntAccount}`}
        className={`tw-autoclose${on ? ' is-on' : ''}${pending ? ' is-pending' : ''}`}
        onClick={flip}
      >
        <span className="tw-autoclose-track" aria-hidden="true"><span className="tw-autoclose-knob" /></span>
        {pending ? (pending === 'on' ? 'Turning on…' : 'Turning off…') : 'Auto-close'}
      </button>
    </InfoTip>
  );
}

/** Toolbar: auto-close for every self-use account in view, all at once. */
function AutoCloseAll({ rows }) {
  const ctx = useContext(BoardActionsContext);
  const eligible = rows.filter(canAutoClose);
  if (!ctx || !eligible.length) return null;
  const onCount = eligible.filter(autoCloseShownOn).length;
  const pending = eligible.some((r) => r.autoClosePending);
  const allOn = onCount === eligible.length;
  const state = allOn ? 'On' : onCount === 0 ? 'Off' : `${onCount} of ${eligible.length}`;
  const flip = () => {
    const next = !allOn;
    const clients = new Set(eligible.map(clientIdOf)).size;
    const who = `${eligible.length} account${eligible.length === 1 ? '' : 's'}${clients > 1 ? ` across ${clients} clients` : ''}`;
    const msg = next
      ? `Turn auto-close on for all ${who}?\n\nWhen a position has no protective stop for the grace period, Tally closes it at market.`
      : `Turn auto-close off for all ${who}?\n\nNaked positions will only raise an alert and stay open until someone acts.`;
    if (next && !ctx.approved) { ctx.autoClose(eligible, true); return; } // the terms pop-up asks first
    if (window.confirm(msg)) ctx.autoClose(eligible, next);
  };
  return (
    <InfoTip align="right" text={`${AUTO_CLOSE_EXPLAINED} This switch turns it on or off for all ${eligible.length} of your accounts shown; switch single accounts on their cards.`}>
      <button
        type="button"
        role="switch"
        aria-checked={allOn}
        aria-label="Auto-close for all your accounts shown"
        className={`tw-autoclose tw-autoclose-all${allOn ? ' is-on' : onCount ? ' is-partial' : ''}${pending ? ' is-pending' : ''}`}
        onClick={flip}
      >
        <span className="tw-autoclose-track" aria-hidden="true"><span className="tw-autoclose-knob" /></span>
        Auto-close <span className="tw-autoclose-state">{pending ? 'Updating…' : state}</span>
      </button>
    </InfoTip>
  );
}

/**
 * Auto-close terms. Auto-close stays off until the trader reads what it does and
 * approves it here; RiskLo records the approval (wording, version, time, IP,
 * browser) and only then lets Tally auto-close their accounts.
 */
function AutoCloseApproval({ count, onCancel, onApproved }) {
  const [terms, setTerms] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cancelRef = useRef(null);

  useEffect(() => {
    let live = true;
    fetch(API_ENDPOINTS.tradeWatcherAutoCloseConsent, { headers: twHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (!live) return;
        if (!data.success) throw new Error(data.error || 'Couldn’t load the auto-close terms');
        setTerms(data.terms);
      })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  const approve = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherAutoCloseConsent, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify({ accept: true, version: terms.version }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t save your approval');
      onApproved();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="tw-modal-root" role="dialog" aria-modal="true" aria-labelledby="tw-ac-title" aria-describedby="tw-ac-desc">
      <button type="button" className="tw-drawer-backdrop" aria-label="Cancel" onClick={busy ? undefined : onCancel} />
      <div className="tw-modal tw-ac-modal">
        <div className="tw-modal-kicker">Auto-close · approval needed</div>
        <h3 id="tw-ac-title">{terms?.title || 'Turn on auto-close?'}</h3>
        {terms ? (
          <>
            <p id="tw-ac-desc" className="tw-modal-copy">{terms.summary}</p>
            <ul className="tw-modal-notes tw-ac-points">
              {terms.points.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <label className="tw-ac-agree">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={busy} />
              <span>{terms.agreement}</span>
            </label>
            <p className="tw-ac-record">
              Your approval is recorded with your email, the time, and this wording. You can turn auto-close off or
              withdraw your approval at any time (Trade Watcher → Settings). See also our{' '}
              <a href="/terms-and-conditions.html" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>.
            </p>
          </>
        ) : !error ? <p className="tw-modal-copy">Loading…</p> : null}
        {error ? <p className="tw-error" role="alert">{error}</p> : null}
        <div className="tw-modal-actions">
          <button type="button" ref={cancelRef} className="vd-btn vd-client-resync-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="vd-btn tw-ac-approve" onClick={approve} disabled={!terms || !agreed || busy}>
            {busy ? 'Saving…' : `Approve and turn on${count > 1 ? ` for ${count} accounts` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Settings: the trader's auto-close approval, with a way to withdraw it. */
function AutoCloseApprovalSettings({ approvedAt, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const withdraw = async () => {
    if (!window.confirm('Withdraw your auto-close approval?\n\nAuto-close turns off on all your accounts with the add-on’s next update. '
      + 'Naked positions will only raise an alert. You can approve again later.')) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherAutoCloseConsent, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify({ accept: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t withdraw your approval');
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="vd-panel vd-ml-widget">
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>Auto-close approval</h3>
          <p className="tw-muted tw-section-sub">
            You approved auto-close{approvedAt ? ` on ${new Date(approvedAt).toLocaleString()}` : ''}. The add-on may close a
            naked position at market on your own self-use accounts where you’ve switched it on. Withdrawing turns it off everywhere.
          </p>
        </div>
        <button type="button" className="tw-danger-btn" onClick={withdraw} disabled={busy}>
          {busy ? 'Withdrawing…' : 'Withdraw approval'}
        </button>
      </div>
      {error ? <p className="tw-error" role="alert">{error}</p> : null}
    </section>
  );
}

/**
 * "Are you sure?" for removing one account or a whole client from the board.
 * target: { scope: 'account' | 'client', row, count }
 */
function RemoveConfirm({ target, busy = false, error = '', onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);
  const { scope, row, count } = target;
  const whole = scope === 'client';
  const plural = count === 1 ? '' : 's';
  return (
    <div className="tw-modal-root" role="alertdialog" aria-modal="true" aria-labelledby="tw-remove-title" aria-describedby="tw-remove-desc">
      <button type="button" className="tw-drawer-backdrop" aria-label="Cancel" onClick={busy ? undefined : onCancel} />
      <div className="tw-modal">
        <div className="tw-modal-kicker">Remove from board</div>
        <h3 id="tw-remove-title">
          {whole ? `Remove ${row.client} and all ${count} account${plural}?` : `Remove account ${row.ntAccount}?`}
        </h3>
        <dl className="tw-modal-facts">
          <div><dt>Client</dt><dd>{row.client}</dd></div>
          {whole
            ? <div><dt>Accounts</dt><dd>{count}</dd></div>
            : <div><dt>Account</dt><dd>{row.ntAccount}</dd></div>}
        </dl>
        <p id="tw-remove-desc" className="tw-modal-copy">
          {whole
            ? 'Every account for this client leaves the board and stops counting in the totals, for everyone who uses Trade Watcher.'
            : 'This account leaves the board and stops counting in the totals, for everyone who uses Trade Watcher.'}
        </p>
        <ul className="tw-modal-notes">
          <li>Nothing changes in NinjaTrader. Its logs are kept.</li>
          <li>It comes back on its own if NinjaTrader reports it connected again or it goes naked.</li>
          <li>You can restore it anytime from Settings → Removed accounts.</li>
        </ul>
        {error ? <p className="tw-error" role="alert">{error}</p> : null}
        <div className="tw-modal-actions">
          <button type="button" ref={cancelRef} className="vd-btn vd-client-resync-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="tw-danger-btn is-solid" onClick={onConfirm} disabled={busy}>
            {busy ? 'Removing…' : whole ? `Remove ${count} account${plural}` : 'Remove account'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Short form for narrow cards (CSS container query picks which one shows). */
const STAT_SHORT_LABELS = { Accumulated: 'Accum.' };

function StatLabel({ label }) {
  const short = STAT_SHORT_LABELS[label];
  if (!short) return <dt>{label}</dt>;
  return (
    <dt title={label}>
      <span className="tw-stat-label-full">{label}</span>
      <span className="tw-stat-label-short">{short}</span>
    </dt>
  );
}

function AccountCard({ row, onLogs }) {
  const problem = isProblem(row);
  return (
    <article className={`tw-card${problem ? ' is-problem' : ''}${row.naked === 'Yes' ? ' is-naked' : ''}`}>
      <header className="tw-card-head">
        <span className="tw-avatar" aria-hidden="true">{initials(row.client)}</span>
        <div className="tw-card-who">
          <div className="tw-client">{row.client}</div>
          <ClientEmail email={row.clientEmail} />
          <div className="tw-acct">{row.ntAccount}</div>
        </div>
        <div className="tw-card-tags">
          <BrokerTag row={row} />
          {isBroker(row) ? null : <CsaTag csa={row.csa} />}
          <BuildBadge row={row} />
        </div>
      </header>

      {problem && row.why ? <p className="tw-why">{row.why}</p> : null}

      {hasTodayPnl(row) ? (
        <div className="tw-running-block">
          <span className="tw-label">Running P/L</span>
          <span className={`tw-running ${pnlClass(row.runningPnl)}`}>{figure(row, row.runningPnl)}</span>
        </div>
      ) : (
        <div className="tw-running-block" title={`${row.brokerName} doesn't report what the account was worth at yesterday's close, so today's change isn't shown.`}>
          <span className="tw-label">Account value</span>
          <span className="tw-running">{figure(row, row.equity)}</span>
        </div>
      )}
      {row.position ? <p className="tw-position">Open: {row.position}</p> : null}

      <dl className="tw-stats">
        {(hasTodayPnl(row)
          ? [['Day', row.dayPnl], ['Open', row.openPnl], ['Accumulated', row.accumulatedPnl]]
          : [['Open P/L', row.openPnl]]
        ).map(([label, v]) => (
          // is-zero lets phones drop an empty Accumulated row to keep cards short.
          <div key={label} className={`tw-stat${!Number(v) ? ' is-zero' : ''}`}>
            <StatLabel label={label} />
            <dd className={pnlClass(v)}>{figure(row, v)}</dd>
          </div>
        ))}
      </dl>

      <StatusChips row={row} />

      <div className="tw-card-foot">
        <span className="tw-muted">
          {row.connected === 'No'
            ? offlineLabel(row)
            : <><span className="tw-foot-prefix">Last seen </span>{agoText(row.lastSeen)}</>}
        </span>
        <div className="tw-foot-actions">
          <AutoCloseSwitch row={row} />
          <LogsButton row={row} onLogs={onLogs} />
        </div>
      </div>
    </article>
  );
}

function AccountTable({ rows, onLogs, showWhy }) {
  const supportView = useContext(SupportViewContext);
  return (
    <div className="tw-table-wrap">
      <table className="tw-table">
        <thead>
          <tr>
            <th>Client / account</th>
            {supportView ? <th>CSA / build</th> : null}
            <th>Status</th>
            <th className="num">Running</th>
            <th className="num">Day</th>
            <th className="num">Open</th>
            <th className="num">Accumulated</th>
            <th>Last seen</th>
            {showWhy ? <th>Why</th> : null}
            <th aria-label="Logs" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className={isProblem(row) ? 'is-problem' : ''}>
              <td>
                <div className="tw-client">{row.client}</div>
                <ClientEmail email={row.clientEmail} />
                <div className="tw-acct">{row.ntAccount} <BrokerTag row={row} /></div>
              </td>
              {supportView ? <td><div className="tw-card-tags">{isBroker(row) ? null : <CsaTag csa={row.csa} />}<BuildBadge row={row} /></div></td> : null}
              <td><StatusChips row={row} /></td>
              <td className={`num ${pnlClass(row.runningPnl)}`}>{figure(row, row.runningPnl)}</td>
              <td className={`num ${pnlClass(row.dayPnl)}`}>{figure(row, row.dayPnl)}</td>
              <td className={`num ${pnlClass(row.openPnl)}`}>{figure(row, row.openPnl)}</td>
              <td className={`num ${pnlClass(row.accumulatedPnl)}`}>{figure(row, row.accumulatedPnl)}</td>
              <td className="tw-muted">
                {row.connected === 'No' ? offlineLabel(row) : agoText(row.lastSeen)}
              </td>
              {showWhy ? <td className="tw-muted tw-why-cell">{row.why || ''}</td> : null}
              <td>
                <div className="tw-foot-actions">
                  <AutoCloseSwitch row={row} />
                  <LogsButton row={row} onLogs={onLogs} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupStatus({ group }) {
  const parts = [];
  if (group.connected) parts.push(`${group.connected} connected`);
  if (group.offline) parts.push(`${group.offline} offline`);
  if (group.naked) parts.push(`${group.naked} naked`);
  return <span className="tw-muted">{parts.join(' · ') || 'No status yet'}</span>;
}

function GroupChips({ group }) {
  return (
    <div className="tw-chips">
      {group.offline
        ? <StatusChip tone="bad">{group.offline === group.accounts.length ? 'Disconnected' : `${group.offline} disconnected`}</StatusChip>
        : <StatusChip tone="good">Connected</StatusChip>}
      {group.naked
        ? <StatusChip tone="bad">{group.naked} naked</StatusChip>
        : <StatusChip tone="good">Protected</StatusChip>}
      <StatusChip tone={group.trading ? 'accent' : 'muted'}>{group.trading ? `${group.trading} trading` : 'No trades'}</StatusChip>
      {group.open ? <StatusChip tone="accent">{group.open} open</StatusChip> : null}
      {(() => {
        const n = group.accounts.filter((r) => r.autoClosedAt).length;
        return n ? <StatusChip tone="warn">{`${n} auto-closed today`}</StatusChip> : null;
      })()}
      <BuildCheckChip check={worstBuildCheck(group.accounts)} />
    </div>
  );
}

function GroupAccountRows({ accounts, onLogs }) {
  return (
    <ul className="tw-group-accounts">
      {accounts.map((row) => (
        <li key={rowKey(row)} className={isProblem(row) ? 'is-problem' : ''}>
          <div className="tw-group-account-main">
            <span className="tw-acct-name">{row.ntAccount} <BrokerTag row={row} /></span>
            <span className="tw-muted">
              {row.connected === 'No' ? offlineLabel(row) : `Last seen ${agoText(row.lastSeen)}`}
            </span>
          </div>
          <StatusChips row={row} />
          <span className={`tw-group-pnl ${pnlClass(row.runningPnl)}`}>{hasTodayPnl(row) ? figure(row, row.runningPnl) : figure(row, row.equity)}</span>
          <div className="tw-foot-actions">
            <AutoCloseSwitch row={row} />
            <LogsButton row={row} onLogs={onLogs} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ClientCard({ group, expanded, onToggle, onLogs }) {
  return (
    <article className={`tw-card tw-group-card${group.problem ? ' is-problem' : ''}${expanded ? ' is-expanded' : ''}`}>
      <header className="tw-card-head">
        <span className="tw-avatar" aria-hidden="true">{initials(group.client)}</span>
        <div className="tw-card-who">
          <div className="tw-client">
            {group.client}
            <span className="tw-count-chip">{group.accounts.length} account{group.accounts.length === 1 ? '' : 's'}</span>
          </div>
          <ClientEmail email={group.clientEmail} />
          <GroupStatus group={group} />
        </div>
        {group.accounts.every(isBroker) ? null : <CsaTag csa={group.csa} />}
      </header>

      <div className="tw-running-block">
        <span className="tw-label">Running P/L · all accounts</span>
        <span className={`tw-running ${pnlClass(group.runningPnl)}`}>{money(group.runningPnl)}</span>
      </div>

      <dl className="tw-stats">
        {[['Day', group.dayPnl], ['Open', group.openPnl], ['Accumulated', group.accumulatedPnl]].map(([label, v]) => (
          <div key={label} className={`tw-stat${!Number(v) ? ' is-zero' : ''}`}>
            <StatLabel label={label} />
            <dd className={pnlClass(v)}>{money(v)}</dd>
          </div>
        ))}
      </dl>

      <GroupChips group={group} />

      <div className="tw-card-foot">
        <span className="tw-muted"><span className="tw-foot-prefix">Last seen </span>{agoText(group.lastSeen)}</span>
        <button type="button" className="tw-text-btn" aria-expanded={expanded} onClick={onToggle}>
          {expanded ? 'Hide accounts' : 'Show accounts'}
        </button>
      </div>

      {expanded ? <GroupAccountRows accounts={group.accounts} onLogs={onLogs} /> : null}
    </article>
  );
}

function ClientTable({ groups, expandedKeys, onToggle, onLogs }) {
  const supportView = useContext(SupportViewContext);
  return (
    <div className="tw-table-wrap">
      <table className="tw-table">
        <thead>
          <tr>
            <th>Client</th>
            {supportView ? <th>CSA</th> : null}
            <th>Accounts</th>
            <th>Status</th>
            <th className="num">Running</th>
            <th className="num">Day</th>
            <th className="num">Open</th>
            <th className="num">Accumulated</th>
            <th>Last seen</th>
            <th aria-label="Accounts" />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const expanded = expandedKeys.has(g.key);
            return (
              <React.Fragment key={g.key}>
                <tr className={g.problem ? 'is-problem' : ''}>
                  <td><div className="tw-client">{g.client}</div><ClientEmail email={g.clientEmail} /></td>
                  {supportView ? <td>{g.accounts.every(isBroker) ? null : <CsaTag csa={g.csa} />}</td> : null}
                  <td className="tw-muted">{g.accounts.length}</td>
                  <td><GroupChips group={g} /></td>
                  <td className={`num ${pnlClass(g.runningPnl)}`}>{money(g.runningPnl)}</td>
                  <td className={`num ${pnlClass(g.dayPnl)}`}>{money(g.dayPnl)}</td>
                  <td className={`num ${pnlClass(g.openPnl)}`}>{money(g.openPnl)}</td>
                  <td className={`num ${pnlClass(g.accumulatedPnl)}`}>{money(g.accumulatedPnl)}</td>
                  <td className="tw-muted">{agoText(g.lastSeen)}</td>
                  <td>
                    <button type="button" className="tw-text-btn" aria-expanded={expanded} onClick={() => onToggle(g.key)}>
                      {expanded ? 'Hide' : 'Show'}
                    </button>
                  </td>
                </tr>
                {expanded ? (
                  <tr className="tw-group-row-detail">
                    <td colSpan={10}><GroupAccountRows accounts={g.accounts} onLogs={onLogs} /></td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AccountsPanel({ title, subtitle, rows, layout, group, empty, onLogs, tone, showWhy }) {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const groups = useMemo(() => (group === 'client' ? groupByClient(rows) : []), [group, rows]);
  const toggle = (key) => setExpandedKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const count = group === 'client' ? groups.length : rows.length;

  let body;
  if (rows.length === 0) {
    body = <p className="tw-empty">{empty}</p>;
  } else if (group === 'client') {
    body = layout === 'list'
      ? <ClientTable groups={groups} expandedKeys={expandedKeys} onToggle={toggle} onLogs={onLogs} />
      : (
        <div className="tw-grid tw-grid--clients">
          {groups.map((g) => (
            <ClientCard key={g.key} group={g} expanded={expandedKeys.has(g.key)} onToggle={() => toggle(g.key)} onLogs={onLogs} />
          ))}
        </div>
      );
  } else {
    body = layout === 'list'
      ? <AccountTable rows={rows} onLogs={onLogs} showWhy={showWhy} />
      : (
        <div className="tw-grid tw-grid--accounts">
          {rows.map((row) => <AccountCard key={rowKey(row)} row={row} onLogs={onLogs} />)}
        </div>
      );
  }

  return (
    <section className={`vd-panel vd-ml-widget tw-section${tone ? ` is-${tone}` : ''}`}>
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>{title}</h3>
          {subtitle ? <p className="tw-muted tw-section-sub">{subtitle}</p> : null}
        </div>
        <span className="tw-count" title={group === 'client' ? `${count} clients` : `${count} accounts`}>{count}</span>
      </div>
      {body}
    </section>
  );
}

function logTone(event) {
  const t = String(event || '').toLowerCase();
  if (t.includes('naked') || t.includes('disconnect') || t.includes('cut')) return 'bad';
  if (t.includes('manual')) return 'warn';
  return 'muted';
}

/** Staff: which RiskLo users (by email) see this client's accounts. */
function ClientLinks({ row, onChanged }) {
  const [emails, setEmails] = useState(row.linkedEmails || []);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async (method, email) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherClientLinks, {
        method,
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify({ feedId: row.feedId, client: row.client, email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not save');
      setEmails(data.emails || []);
      if (method === 'POST') setDraft('');
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="tw-csa-form" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) send('POST', draft.trim()); }}>
      <label className="vd-control-label" htmlFor="tw-link-input">Who sees {row.client}</label>
      <p className="tw-muted tw-links-help">
        Pro users with a linked email see only this client's accounts. Admin and Support always see everyone.
      </p>
      {emails.length ? (
        <ul className="tw-links">
          {emails.map((email) => (
            <li key={email}>
              <span>{email}</span>
              <button type="button" className="tw-icon-btn" aria-label={`Remove ${email}`} disabled={busy} onClick={() => send('DELETE', email)}>
                <IconX size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="tw-muted tw-links-help">No one linked yet.</p>
      )}
      <div className="tw-inline">
        <input
          id="tw-link-input"
          className="tw-input"
          type="email"
          value={draft}
          placeholder="client@email.com"
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="vd-btn" disabled={busy || !draft.trim()}>Link</button>
      </div>
      {error ? <p className="tw-error">{error}</p> : null}
    </form>
  );
}

function LogsDrawer({
  row, onClose, onCsaSaved, canManage = false, onLinksChanged,
  canRemove = false, clientAccountCount = 1, onRemove, onOpenCsas,
}) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [csa, setCsa] = useState(row.csa || '');
  const [saving, setSaving] = useState(false);
  // CSAs come from the CSAs tab: pick one, never type a new name here.
  const [csaOptions, setCsaOptions] = useState(null);

  useEffect(() => {
    if (!canManage) return undefined;
    let cancelled = false;
    fetch(API_ENDPOINTS.tradeWatcherCsas, { headers: twHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setCsaOptions(data.success ? (data.csas || []).map((c) => ({ name: c.name, email: c.email })) : []);
      })
      .catch(() => { if (!cancelled) setCsaOptions([]); });
    return () => { cancelled = true; };
  }, [canManage]);

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.tradeWatcherLogs(row.feedId, row.ntAccount), { headers: twHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success) setRows(data.rows || []);
        else setError(data.error || 'Failed to load logs');
      })
      .catch(() => { if (!cancelled) setError('Failed to load logs'); });
    return () => { cancelled = true; };
  }, [row.feedId, row.ntAccount]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const saveCsa = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherCsa, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify({ clientId: clientIdOf(row), csa }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      onCsaSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-root" role="dialog" aria-modal="true" aria-label={`Logs for ${row.ntAccount}`}>
      <button type="button" className="tw-drawer-backdrop" aria-label="Close logs" onClick={onClose} />
      <aside className="tw-drawer">
        <header className="tw-drawer-head">
          <span className="tw-avatar" aria-hidden="true">{initials(row.client)}</span>
          <div className="tw-card-who">
            <div className="tw-client">{row.client}</div>
            <div className="tw-acct">{row.ntAccount}</div>
          </div>
          <button type="button" className="tw-icon-btn" onClick={onClose} aria-label="Close"><IconX size={18} /></button>
        </header>
        <div className="tw-drawer-summary">
          <StatusChips row={row} />
          <div className="tw-drawer-pnl">
            <span className="tw-label">Running P/L</span>
            <span className={`tw-running ${pnlClass(row.runningPnl)}`}>{money(row.runningPnl)}</span>
          </div>
        </div>
        {canManage ? (
          <>
            <form className="tw-csa-form" onSubmit={saveCsa}>
              <label className="vd-control-label" htmlFor="tw-csa-input">CSA for {row.client}</label>
              <div className="tw-inline">
                <select
                  id="tw-csa-input"
                  className="tw-input"
                  value={csa}
                  onChange={(e) => setCsa(e.target.value)}
                  disabled={csaOptions === null}
                >
                  <option value="">{csaOptions === null ? 'Loading CSAs…' : 'Unassigned'}</option>
                  {/* Keep a current CSA selectable even if it's no longer listed. */}
                  {csa && csaOptions && !csaOptions.some((o) => o.name.toLowerCase() === csa.toLowerCase())
                    ? <option value={csa}>{csa}</option>
                    : null}
                  {(csaOptions || []).map((o) => (
                    <option key={o.name} value={o.name}>{o.email ? `${o.name} · ${o.email}` : `${o.name} (no email)`}</option>
                  ))}
                </select>
                <button type="submit" className="vd-btn" disabled={saving || (csa || '') === (row.csa || '')}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
              {csaOptions && csaOptions.length === 0 ? (
                <p className="tw-muted tw-links-help">
                  No CSAs yet.{' '}
                  {onOpenCsas ? <button type="button" className="tw-link" onClick={onOpenCsas}>Add one on the CSAs tab</button> : 'Add one on the CSAs tab.'}
                </p>
              ) : null}
            </form>
            <ClientLinks row={row} onChanged={onLinksChanged || (() => {})} />
          </>
        ) : null}
        {canRemove && onRemove ? (
          <div className="tw-drawer-remove">
            <div>
              <span className="vd-control-label">Remove from board</span>
              <p className="tw-muted">
                For accounts that are gone for good. Removed accounts stop counting in the totals and can be restored from Settings.
              </p>
            </div>
            <div className="tw-inline tw-remove-actions">
              <button type="button" className="tw-danger-btn" onClick={() => onRemove('account')}>Remove this account</button>
              {clientAccountCount > 1 ? (
                <button type="button" className="tw-danger-btn" onClick={() => onRemove('client')}>
                  Remove all {clientAccountCount} accounts for {row.client}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="tw-drawer-body">
          <span className="vd-control-label">Activity</span>
          {error ? <p className="tw-error">{error}</p> : null}
          {rows === null && !error ? <p className="tw-empty">Loading logs…</p> : null}
          {rows && rows.length === 0 ? <p className="tw-empty">No log events yet for this account.</p> : null}
          {rows && rows.length > 0 ? (
            <ol className="tw-timeline">
              {rows.map((r, i) => (
                <li key={`${r.occurredAt}-${i}`} className={`is-${logTone(r.event)}`}>
                  <div className="tw-timeline-head">
                    <StatusChip tone={logTone(r.event)}>{r.event}</StatusChip>
                    <span className="tw-muted">{fmtTime(r.occurredAt)}</span>
                  </div>
                  {r.detail ? <p className="tw-timeline-detail">{r.detail}</p> : null}
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function prettyBody(body) {
  if (!body) return '(empty body)';
  try { return JSON.stringify(JSON.parse(body), null, 2); } catch (_e) { return body; }
}

/** canAdmin: Full Admin extras (recorder, clear board). Everyone gets their own URL. */
/**
 * The NinjaTrader add-on: install steps and the feed URL (shown in the setup
 * pop-up on Connections), and Full Admin's recorder (Settings).
 */
function SetupView({ onChanged, canAdmin = false, canManage = false, showInstall = true, showRecorder = true }) {
  const [token, setToken] = useState(null);
  const [lastPayloadAt, setLastPayloadAt] = useState(null);
  const [payloads, setPayloads] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [status, setStatus] = useState('');
  const [addonVersions, setAddonVersions] = useState({});

  // Current add-on versions, read by the server from the zips it serves.
  useEffect(() => {
    let alive = true;
    fetch(API_ENDPOINTS.tradeWatcherAddonInfo)
      .then((r) => r.json())
      .then((d) => { if (alive && d?.success) setAddonVersions(d.packages || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const selfUseVersion = addonVersions['self-use']?.version;
  const csaVersion = addonVersions.csa?.version;

  const loadFeed = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherFeed, { method: 'POST', headers: twHeaders() });
      const data = await res.json();
      if (data.success) { setToken(data.token); setLastPayloadAt(data.lastPayloadAt); }
      else setStatus(data.error || 'Could not load your feed URL');
    } catch (_e) { setStatus('Could not load your feed URL'); }
  }, []);

  const loadPayloads = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherPayloads, { headers: twHeaders() });
      const data = await res.json();
      if (data.success) setPayloads(data.payloads || []);
    } catch (_e) { /* optional */ }
  }, []);

  useEffect(() => {
    loadFeed();
    if (canAdmin) loadPayloads();
    const t = setInterval(() => { if (canAdmin && document.visibilityState === 'visible') loadPayloads(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [loadFeed, loadPayloads, canAdmin]);

  const feedUrl = token ? API_ENDPOINTS.tradeWatcherIngestUrl(token) : '';
  const configLines = token ? `DashboardUrl=${feedUrl}\nDashboardKey=risklo\nDashboardEveryMinutes=1` : '';

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setStatus(`${label} copied.`);
    } catch (_e) {
      setStatus('Copy failed — select the text and copy it manually.');
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Make a new feed URL? The old one stops working immediately, so NinjaTrader needs the new one.')) return;
    const res = await fetch(API_ENDPOINTS.tradeWatcherFeedRegenerate, { method: 'POST', headers: twHeaders() });
    const data = await res.json().catch(() => ({}));
    if (data.success) { setToken(data.token); setStatus('New feed URL created. Paste it into the add-on window (Change → Connect).'); }
  };

  const clear = async () => {
    if (!window.confirm('Clear your feed’s accounts, logs and recordings from the board? The URL stays the same.')) return;
    const res = await fetch(API_ENDPOINTS.tradeWatcherFeedClear, { method: 'POST', headers: twHeaders() });
    const data = await res.json().catch(() => ({}));
    if (data.success) { setPayloads([]); setStatus('Cleared.'); onChanged(); }
  };

  const connected = Boolean(lastPayloadAt);

  return (
    <>
      {showInstall ? (
      <>
      <section className="vd-panel vd-ml-widget">
        <div className="vd-ml-widget-header tw-section-head">
          <div>
            <h3>
              NinjaTrader add-on
              {selfUseVersion ? <span className="tw-version-chip">v{selfUseVersion}</span> : null}
            </h3>
            <p className="tw-muted tw-section-sub">
              No compiling and no config files: import it, paste your feed URL, choose which accounts to send.
            </p>
          </div>
          <a className="vd-btn vd-client-resync-btn" href={API_ENDPOINTS.tradeWatcherAddonDownload} download>
            Download add-on{selfUseVersion ? ` v${selfUseVersion}` : ''}
          </a>
        </div>

        <ol className="tw-steps">
          <li>
            <div className="tw-step-title">Download the add-on</div>
            <p className="tw-muted">A zip file. Don’t unzip it.</p>
          </li>
          <li>
            <div className="tw-step-title">Import it into NinjaTrader</div>
            <p className="tw-muted">
              In the Control Center choose <strong>Tools → Import → NinjaScript Add-On</strong> and pick the zip.
              Restart NinjaTrader if it asks.
            </p>
            <details className="tw-manual-tally">
              <summary>Updating from an older version?</summary>
              <p className="tw-muted">
                Remove the old one first, or the import fails with &ldquo;duplicate method names&rdquo;:
              </p>
              <ul className="tw-muted">
                <li>
                  <strong>Tools → Remove NinjaScript Assembly…</strong>, remove RiskLoTally_SelfUse (or RiskLoTally_CSA),
                  restart NinjaTrader, then import the new zip.
                </li>
                <li>
                  Not in that list (an older install that came as source files)? Delete <code>Tally.cs</code> and the
                  {' '}<code>RiskLoTally…cs</code> files from Documents\NinjaTrader 8\bin\Custom\AddOns, open the NinjaScript
                  Editor and press F5, then import.
                </li>
              </ul>
              <p className="tw-muted">Your connection and account choices are kept: they live in the Tally folder, not the add-on.</p>
            </details>
          </li>
          <li>
            <div className="tw-step-title">Paste your feed URL</div>
            <p className="tw-muted">
              The RiskLo Tally window opens by itself on first launch (later: <strong>Tools → RiskLo Tally</strong>).
              Click <strong>Copy URL</strong> below, paste it there and click <strong>Connect</strong>. No key or email needed.
            </p>
          </li>
          <li>
            <div className="tw-step-title">Choose accounts</div>
            <p className="tw-muted">
              Accounts are sent once they connect, and stay on {appName()} while offline so a disconnect shows, even the next morning. Old or blown accounts that never connect stay off. Set any account you don’t want on {appName()} to Never in the add-on.
            </p>
          </li>
        </ol>

        {canManage ? (
          <div className="tw-other-downloads">
            <span className="vd-control-label">Other download (staff)</span>
            <ul>
              <li>
                <a href={`${API_ENDPOINTS.tradeWatcherAddonDownload}?package=csa`} download>
                  CSA alert-only build{csaVersion ? ` v${csaVersion}` : ''}
                </a>
                <span className="tw-muted">
                  {' '}for CSA-monitored clients. It alerts only and can never close, change or cancel a trade.
                  Never give the standard download to a monitored client.
                </span>
              </li>
            </ul>
          </div>
        ) : null}
        {canAdmin ? <TallyTwoDownloads /> : null}
      </section>

      <section className="vd-panel vd-ml-widget">
        <div className="vd-ml-widget-header tw-section-head">
          <div>
            <h3>Connect NinjaTrader</h3>
            <p className="tw-muted tw-section-sub">
              Your private feed URL, for the add-on above. Keep it private — anyone with it can post to your board.
            </p>
          </div>
          <span className={`tw-chip is-${connected ? 'good' : 'muted'}`}>
            {connected ? `Receiving · ${agoText(lastPayloadAt)}` : 'Waiting for first update'}
          </span>
        </div>

        <details className="tw-manual-tally">
          <summary>Using a hand-installed Tally.cs instead?</summary>
          <p className="tw-muted">
            Copy these lines over the three <code>Dashboard…</code> lines in{' '}
            <code>Documents\NinjaTrader 8\Tally\Tally.config.txt</code>, save, and Tally picks them up within a minute.
          </p>
          <div className="tw-code-row">
            <pre className="tw-code">{configLines || 'Loading…'}</pre>
            <button type="button" className="vd-btn" disabled={!token} onClick={() => copy(configLines, 'Config lines')}>
              <IconCopy size={14} /> Copy
            </button>
          </div>
        </details>

        <div className="tw-setup-actions">
          <div className="tw-inline tw-grow">
            <input className="tw-input tw-grow" readOnly value={feedUrl || 'Loading…'} aria-label="Feed URL" onFocus={(e) => e.target.select()} />
            <button type="button" className="vd-btn vd-client-resync-btn" disabled={!token} onClick={() => copy(feedUrl, 'Feed URL')}>
              <IconCopy size={14} /> Copy URL
            </button>
          </div>
          <button type="button" className="vd-btn vd-client-resync-btn" onClick={regenerate}>New URL</button>
          {canAdmin ? (
            <button type="button" className="vd-btn vd-client-resync-btn" onClick={clear}>Clear my board</button>
          ) : null}
        </div>
        {status ? <p className="tw-muted tw-status">{status}</p> : null}
      </section>
      </>
      ) : null}

      {canAdmin && showRecorder ? (
      <section className="vd-panel vd-ml-widget">
        <div className="vd-ml-widget-header tw-section-head">
          <div>
            <h3>Recorder</h3>
            <p className="tw-muted tw-section-sub">Latest requests from NinjaTrader, newest first.</p>
          </div>
          <button type="button" className="vd-btn vd-client-resync-btn" onClick={loadPayloads}>
            <IconRefresh size={14} /> Refresh
          </button>
        </div>
        {payloads.length === 0 ? (
          <p className="tw-empty">Nothing received yet. Once NinjaTrader sends an update it appears here within seconds.</p>
        ) : (
          <ul className="tw-recorder">
            {payloads.map((p) => (
              <li key={p.id} className={expanded === p.id ? 'is-open' : ''}>
                <button type="button" className="tw-recorder-row" aria-expanded={expanded === p.id} onClick={() => setExpanded((id) => (id === p.id ? null : p.id))}>
                  <span className="tw-recorder-time">{fmtTime(p.received_at)}</span>
                  <span className="tw-chip is-muted">{p.method}</span>
                  <span className="tw-muted">{p.content_type || 'no content type'}</span>
                  <span className={`tw-chip is-${p.accounts_seen || p.events_seen ? 'good' : 'warn'}`}>
                    {p.accounts_seen} account{p.accounts_seen === 1 ? '' : 's'} · {p.events_seen} event{p.events_seen === 1 ? '' : 's'}
                  </span>
                </button>
                {expanded === p.id ? (
                  <pre className="tw-code tw-raw">
                    {p.query && Object.keys(p.query).length ? `Query: ${JSON.stringify(p.query, null, 2)}\n\n` : ''}
                    {prettyBody(p.body)}
                  </pre>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

/** Newest "last update" among accounts (removed accounts keep updating in the background). */
function latestSeen(accounts) {
  return accounts.map((a) => a.lastSeen).filter(Boolean).sort().pop() || null;
}

/** Accounts removed from the board, grouped by client, with Restore. Full Admin: all; Support: hands-off (CSA) accounts in their company. */
function RemovedAccounts({ onChanged }) {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [openClient, setOpenClient] = useState('');

  const loadRemoved = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherRemoved, { headers: twHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load removed accounts');
      setAccounts(data.accounts || []);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { loadRemoved(); }, [loadRemoved]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const a of accounts || []) {
      const key = clientIdOf(a);
      if (!map.has(key)) map.set(key, { key, client: a.client, accounts: [] });
      map.get(key).accounts.push(a);
    }
    return [...map.values()];
  }, [accounts]);

  const restore = async (body, key) => {
    setBusyKey(key);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherRestore, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Restore failed');
      await loadRemoved();
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  return (
    <section className="vd-panel vd-ml-widget">
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>Removed accounts</h3>
          <p className="tw-muted tw-section-sub">
            Taken off the board by an admin, for everyone. Removed accounts keep receiving updates in the background, so a restored account shows its latest data. One comes back on its own if it reconnects or goes naked.
          </p>
        </div>
        {accounts ? <span className="tw-chip is-muted">{accounts.length} removed</span> : null}
      </div>
      {error ? <p className="tw-error" role="alert">{error}</p> : null}
      {accounts === null && !error ? <p className="tw-empty">Loading…</p> : null}
      {accounts && accounts.length === 0 ? <p className="tw-empty">Nothing removed.</p> : null}
      {groups.length > 0 ? (
        <ul className="tw-removed-list">
          {groups.map((g) => (
            <li key={g.key} className="tw-removed-client">
              <div className="tw-removed-row">
                <button
                  type="button"
                  className="tw-link"
                  aria-expanded={openClient === g.key}
                  onClick={() => setOpenClient((k) => (k === g.key ? '' : g.key))}
                >
                  {g.client} · {g.accounts.length} account{g.accounts.length === 1 ? '' : 's'}
                </button>
                <span className="tw-muted tw-removed-latest">last update {agoText(latestSeen(g.accounts))}</span>
                <button
                  type="button"
                  className="tw-text-btn"
                  disabled={Boolean(busyKey)}
                  onClick={() => restore({ clientId: g.key }, `client:${g.key}`)}
                >
                  {busyKey === `client:${g.key}` ? 'Restoring…' : g.accounts.length > 1 ? 'Restore all' : 'Restore'}
                </button>
              </div>
              {openClient === g.key ? (
                <ul className="tw-removed-accounts">
                  {g.accounts.map((a) => {
                    const key = `acct:${a.feedId}:${a.ntAccount}`;
                    return (
                      <li key={key} className="tw-removed-row">
                        <span>
                          {a.ntAccount}
                          <span className="tw-muted"> · removed {fmtTime(a.removedAt)}{a.removedBy ? ` by ${a.removedBy}` : ''}</span>
                          <span className="tw-muted"> · last update {agoText(a.lastSeen)}</span>
                        </span>
                        <button
                          type="button"
                          className="tw-text-btn"
                          disabled={Boolean(busyKey)}
                          onClick={() => restore({ feedId: a.feedId, ntAccount: a.ntAccount }, key)}
                        >
                          {busyKey === key ? 'Restoring…' : 'Restore'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Full Admin: who sees self-use accounts (no CSA, not the alert-only build).
 * Hands-off accounts always show to Support and up; this decides whether
 * self-use ones do too, or only Full Admin sees them.
 */
function SelfUseVisibilitySettings({ onChanged }) {
  const [adminOnly, setAdminOnly] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    let alive = true;
    fetch(API_ENDPOINTS.tradeWatcherSelfUseVisibility, { headers: twHeaders() })
      .then((r) => r.json().catch(() => ({})))
      .then((d) => { if (alive && d.success) setAdminOnly(Boolean(d.adminOnly)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const choose = async (next) => {
    if (next === adminOnly || busy) return;
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherSelfUseVisibility, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify({ adminOnly: next }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t save');
      setAdminOnly(Boolean(d.adminOnly));
      setSaved('Saved.');
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="vd-panel vd-ml-widget">
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>Who sees self-use accounts</h3>
          <p className="tw-muted tw-section-sub">
            Hands-off (CSA) accounts are always shown to Support and up. Self-use accounts are traders’ own accounts
            running the self-use add-on or a connected broker.
          </p>
        </div>
      </div>
      <div className="tw-choice-list" role="radiogroup" aria-label="Who sees self-use accounts">
        <label className={`tw-choice${adminOnly === false ? ' is-active' : ''}`}>
          <input type="radio" name="tw-self-use" checked={adminOnly === false} disabled={busy || adminOnly === null} onChange={() => choose(false)} />
          <span>
            <b>Support and up</b>
            <small className="tw-muted">Support and Brand Admins see every account in their company, self-use included.</small>
          </span>
        </label>
        <label className={`tw-choice${adminOnly === true ? ' is-active' : ''}`}>
          <input type="radio" name="tw-self-use" checked={adminOnly === true} disabled={busy || adminOnly === null} onChange={() => choose(true)} />
          <span>
            <b>Full Admin only</b>
            <small className="tw-muted">Support and Brand Admins see hands-off accounts and their own accounts only. Only you see every account.</small>
          </span>
        </label>
      </div>
      {saved ? <p className="tw-muted" role="status">{saved}</p> : null}
      {error ? <p className="tw-error" role="alert">{error}</p> : null}
    </section>
  );
}

function BoardSettings({ hideDays, onChange }) {
  const [draft, setDraft] = useState(String(hideDays));
  const [saved, setSaved] = useState('');

  useEffect(() => { setDraft(String(hideDays)); }, [hideDays]);

  const save = (e) => {
    e.preventDefault();
    const n = Math.max(0, Math.min(365, Math.floor(Number(draft))));
    if (!Number.isFinite(n)) return;
    onChange(n);
    setSaved(n === 0 ? 'Saved. Long-offline accounts are always shown.' : `Saved. Accounts offline ${n}+ days are hidden.`);
  };

  return (
    <section className="vd-panel vd-ml-widget">
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>Board settings</h3>
          <p className="tw-muted tw-section-sub">Saved in this browser for your account view.</p>
        </div>
      </div>
      <form className="tw-setting-row" onSubmit={save}>
        <div className="tw-setting-copy">
          <label className="tw-step-title" htmlFor="tw-hide-days">Hide accounts offline longer than</label>
          <p className="tw-muted">Old or retired accounts stop cluttering the board. Set 0 to always show them. You can still reveal them from the Board.</p>
        </div>
        <div className="tw-inline tw-setting-control">
          <div className="tw-days-input">
            <input
              id="tw-hide-days"
              className="tw-input"
              type="number"
              min="0"
              max="365"
              inputMode="numeric"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setSaved(''); }}
            />
            <span className="tw-muted">days</span>
          </div>
          <button type="submit" className="vd-btn">Save</button>
        </div>
      </form>
      {saved ? <p className="tw-muted tw-status">{saved}</p> : null}
    </section>
  );
}

/** Trade Watcher is Pro and up; Basic users who can see it in the menu get this. */
/** Made-up accounts for the Pro preview (never real data). */
function sampleRows() {
  const now = Date.now();
  const row = (ntAccount, client, extra) => ({
    feedId: 0, client, ntAccount, connected: 'Yes', naked: 'No', trading: 'Yes', clockOk: 'Yes',
    dayPnl: 0, openPnl: 0, accumulatedPnl: 0, runningPnl: 0, position: null, openPosition: false,
    lastSeen: new Date(now - 40000).toISOString(), offlineSince: null, offlineDays: 0, why: null,
    autoClose: null, ...extra,
  });
  return [
    row('APEX-50K-01', 'You', { dayPnl: 412.5, runningPnl: 412.5, accumulatedPnl: 1830 }),
    row('APEX-50K-02', 'You', { naked: 'Yes', openPnl: -86, runningPnl: 126, dayPnl: 212, openPosition: true, position: 'MNQ Long 2', why: 'Naked: open position without a protective stop' }),
    row('TOPSTEP-150K', 'You', { dayPnl: -145, runningPnl: -145, trading: 'Yes', openPosition: false }),
    row('TPT-100K-07', 'You', { connected: 'No', trading: 'No', offlineSince: new Date(now - 2 * 3600000).toISOString(), why: 'Disconnected in NinjaTrader' }),
    row('FFN-75K-03', 'You', { dayPnl: 980, runningPnl: 1022, openPnl: 42, openPosition: true, position: 'ES Short 1' }),
    row('BULENOX-25K', 'You', { trading: 'No' }),
  ];
}

/**
 * Below Pro: the real board layout with sample accounts, blurred behind the same
 * "Upgrade to RiskLo Pro" card as the other Pro features. No data is loaded.
 */
export function TradeWatcherProLock({ onUpgrade }) {
  const rows = sampleRows();
  const kpis = [
    { label: 'Accounts', value: rows.length, note: `reporting to ${appName()}` },
    { label: 'Connected', value: 5, tone: 'good', note: `of ${rows.length}` },
    { label: 'Disconnected', value: 1, tone: 'bad', note: 'need a check-in' },
    { label: 'Naked', value: 1, tone: 'bad', note: 'no protective stop' },
    { label: 'Trading today', value: 4, tone: 'accent', note: '2 open · 2 closed' },
  ];
  return (
    <div className="vd-page tw-page">
      <div className="vd-cr-titlebar">
        <PageHeading
          title="Trade Watcher"
          description="Live NinjaTrader health for your accounts: connections, naked positions and P/L."
        />
      </div>
      <div className="bulk-calculator-locked dashboard-pro-lock tw-pro-lock">
        <div className="pro-lock-overlay">
          <div className="pro-lock-content">
            <div className="pro-lock-icon">
              <IconLock size={48} />
            </div>
            <h3 className="pro-lock-title">Trade Watcher</h3>
            <p className="pro-lock-subtitle">This feature is available in {appName()} Pro</p>
            <p className="pro-lock-description">
              See your NinjaTrader accounts live, get alerted the moment a position is left without a stop, and review
              every account&apos;s activity.
            </p>
            <button type="button" className="pro-lock-upgrade-button" onClick={() => onUpgrade && onUpgrade()}>
              Upgrade to {appName()} Pro
            </button>
          </div>
        </div>
        <div className="dashboard-pro-lock-preview" aria-hidden="true">
          <div className="tw-kpis">
            {kpis.map((k) => (
              <div key={k.label} className={`tw-kpi${k.tone ? ` is-${k.tone}` : ''}`}>
                <span className="tw-label">{k.label}</span>
                <span className="tw-kpi-value">{k.value}</span>
                <span className="tw-muted tw-kpi-note">{k.note}</span>
              </div>
            ))}
          </div>
          <AccountsPanel
            title="Needs attention"
            subtitle="Disconnected, naked, or NinjaTrader not set to Eastern time."
            rows={rows.filter(isProblem)}
            layout="cards"
            group="account"
            tone="alert"
            empty=""
            onLogs={() => {}}
          />
          <AccountsPanel
            title="Accounts"
            subtitle={`${rows.length} of ${rows.length} shown`}
            rows={rows}
            layout="cards"
            group="account"
            empty=""
            onLogs={() => {}}
          />
        </div>
      </div>
    </div>
  );
}

/** ?tab=connections (e.g. the dashboard widget's "Connect more accounts"): open that tab once. */
const TABS = ['board', 'csas', 'connections', 'alerts', 'settings'];

function readTabParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (!tab) return null;
    params.delete('tab');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    return TABS.includes(tab) ? tab : null;
  } catch (_e) { return null; }
}

function readConnectReturn() {
  try {
    const params = new URLSearchParams(window.location.search);
    const status = String(params.get('status') || '').toUpperCase();
    const fromSnaptrade = Boolean(params.get('connection_id')) || params.has('snaptrade');
    const tradovate = params.get('tradovate');
    const snaptradeOk = status === 'SUCCESS' || params.get('snaptrade') === 'connected';
    if (fromSnaptrade && !snaptradeOk && !status) return null;
    if (!fromSnaptrade && !tradovate) return null;
    ['snaptrade', 'status', 'connection_id', 'error_code', 'statusCode', 'tradovate', 'reason', 'ctx', 'syncJob', 'tvMode']
      .forEach((k) => params.delete(k));
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    if (tradovate) return { kind: 'tradovate', failed: tradovate !== 'success' };
    return { kind: 'snaptrade', failed: !snaptradeOk, abandoned: status === 'ABANDONED' };
  } catch (_e) { return null; }
}

export default function TradeWatcher({ onNavigate } = {}) {
  const { adminViewAs, isAdminActual } = useAuth();
  const previewTier = isAdminActual && adminViewAs && adminViewAs !== 'admin' ? adminViewAs : null;
  previewViewAs = previewTier;
  // Back from a broker's sign-in page (SnapTrade ?status=SUCCESS&connection_id=…
  // or ?snaptrade=connected; Tradovate ?tradovate=success|error): open
  // Connections, which pulls the new accounts onto the board.
  const [connectReturn] = useState(readConnectReturn);
  // The tab survives a refresh (sessionStorage); a sign-in return opens Connections.
  const [tab, setTab] = usePersistentTab('tradeWatcher.tab', 'board', TABS);
  const [tabParam] = useState(readTabParam);
  useEffect(() => {
    if (connectReturn) setTab('connections');
    else if (tabParam) setTab(tabParam);
  }, [connectReturn, tabParam, setTab]);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  // ?account=<NinjaTrader account> (from the dashboard widgets): open the board
  // searched to that account, then drop it from the address bar.
  const [q, setQ] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const account = params.get('account');
      if (!account) return '';
      params.delete('account');
      const rest = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
      return account;
    } catch (_e) { return ''; }
  });
  const [csa, setCsa] = useState('');
  const [buildSel, setBuildSel] = useState(''); // Support view: '' | 'self' | 'hands'
  const [clientSel, setClientSel] = useState('');
  const [tradeState, setTradeState] = useState('all');
  const [view, setView] = useState('all');
  const [layout, setLayout] = useState(readLayout);
  const [group, setGroup] = useState(readGroup);
  const [hideDays, setHideDays] = useState(readHideDays);
  const [showHidden, setShowHidden] = useState(false);
  const [logsRow, setLogsRow] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [removeNotice, setRemoveNotice] = useState('');
  const [, setTick] = useState(0);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherBoard, { headers: twHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load board');
      setBoard(data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Switching "Preview as" reloads the board for that tier (skips first render;
  // the polling effect below does the initial load).
  const lastPreviewRef = useRef(previewTier);
  useEffect(() => {
    if (lastPreviewRef.current === previewTier) return;
    lastPreviewRef.current = previewTier;
    load();
  }, [previewTier, load]);

  useEffect(() => {
    load();
    const poll = setInterval(() => { if (document.visibilityState === 'visible') load(); }, REFRESH_MS);
    const clock = setInterval(() => setTick((t) => t + 1), 5000); // keep "x ago" fresh
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [load]);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // What this viewer may do (server decides; least privilege until it answers).
  const viewer = board?.viewer || { scope: 'own', role: 'client', canManage: false, canConfigureFeed: false };
  // A remembered tab this viewer can't see (e.g. after "Preview as") goes back to the board.
  const canSeeTab = tab === 'csas' ? viewer.canManage
    : tab === 'alerts' ? Boolean(viewer.canAdminFeed)
      : tab === 'settings' ? (viewer.canManage || viewer.autoCloseApproved) : true;
  useEffect(() => { if (board && !canSeeTab) setTab('board'); }, [board, canSeeTab, setTab]);

  // Full Admin: open unprotected-position alerts, for the Alerts tab's badge.
  const [alertCount, setAlertCount] = useState(0);
  const canSeeAlerts = Boolean(viewer.canAdminFeed);
  useEffect(() => {
    if (!canSeeAlerts) { setAlertCount(0); return undefined; }
    let live = true;
    const loadCount = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.tradeWatchAlertsCount, { headers: getBearerAuthHeaders() });
        const data = await res.json().catch(() => ({}));
        if (live && res.ok && data.success) setAlertCount(Number(data.count) || 0);
      } catch (_e) { /* the badge is optional */ }
    };
    loadCount();
    const poll = setInterval(() => { if (document.visibilityState === 'visible') loadCount(); }, 30000);
    window.addEventListener('trade-watch-changed', loadCount);
    return () => { live = false; clearInterval(poll); window.removeEventListener('trade-watch-changed', loadCount); };
  }, [canSeeAlerts]);

  const [autoCloseNote, setAutoCloseNote] = useState('');
  const [approvalFor, setApprovalFor] = useState(null); // rows waiting for the terms pop-up
  const autoCloseApproved = Boolean(viewer.autoCloseApproved);
  const sendAutoClose = useCallback(async (rows, on, { approvedNow = false } = {}) => {
    const targets = rows.filter(canAutoClose).map((r) => ({ feedId: r.feedId, ntAccount: r.ntAccount }));
    if (!targets.length) return;
    if (on && !autoCloseApproved && !approvedNow) {
      setApprovalFor(rows);
      return;
    }
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherAutoClose, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify({ on, targets }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.needsApproval) {
        setApprovalFor(rows);
        return;
      }
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t change auto-close');
      const n = data.accounts;
      setAutoCloseNote(`Auto-close ${on ? 'on' : 'off'} sent to NinjaTrader for ${n} account${n === 1 ? '' : 's'}. `
        + 'It takes effect with the add-on’s next update (within a minute at the usual setting) and shows here once NinjaTrader confirms.'
        + (data.skipped ? ` ${data.skipped} skipped: they need the latest self-use add-on.` : ''));
      load();
    } catch (e) {
      setAutoCloseNote(e.message);
    }
  }, [load, autoCloseApproved]);

  const boardActions = useMemo(
    () => ({ autoClose: sendAutoClose, approved: autoCloseApproved }),
    [sendAutoClose, autoCloseApproved],
  );

  const chooseGroup = (next) => {
    setGroup(next);
    try { localStorage.setItem(GROUP_KEY, next); } catch (_e) { /* per-viewer convenience only */ }
  };

  const chooseLayout = (next) => {
    setLayout(next);
    try { localStorage.setItem(LAYOUT_KEY, next); } catch (_e) { /* per-viewer convenience only */ }
  };

  const chooseHideDays = (days) => {
    setHideDays(days);
    setShowHidden(false);
    try { localStorage.setItem(HIDE_DAYS_KEY, String(days)); } catch (_e) { /* per-viewer convenience only */ }
  };

  const everything = useMemo(() => board?.live || [], [board]);

  // Remove from board (Full Admin). A client is matched the way "By client" groups it.
  const clientAccountCount = useCallback((row) => {
    const key = clientIdOf(row);
    return everything.filter((r) => clientIdOf(r) === key).length;
  }, [everything]);

  const askRemove = useCallback((row, scope) => {
    setRemoveError('');
    setRemoveTarget({ scope, row, count: scope === 'client' ? clientAccountCount(row) : 1 });
  }, [clientAccountCount]);

  const runRemove = useCallback(async () => {
    if (!removeTarget) return;
    const { scope, row } = removeTarget;
    setRemoving(true);
    setRemoveError('');
    try {
      const res = await fetch(API_ENDPOINTS.tradeWatcherRemove, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...twHeaders() },
        body: JSON.stringify(scope === 'client'
          ? { clientId: clientIdOf(row) }
          : { feedId: row.feedId, ntAccount: row.ntAccount }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Remove failed');
      const n = Number(data.removed) || 0;
      setRemoveNotice(scope === 'client'
        ? `Removed ${row.client} (${n} account${n === 1 ? '' : 's'}) from the board.`
        : `Removed ${row.ntAccount} from the board.`);
      setRemoveTarget(null);
      setLogsRow(null);
      load();
    } catch (err) {
      setRemoveError(err.message);
    } finally {
      setRemoving(false);
    }
  }, [removeTarget, load]);
  const hiddenCount = everything.filter((r) => isLongOffline(r, hideDays)).length;
  // Long-offline accounts drop out of the counts and lists unless revealed.
  const all = showHidden ? everything : everything.filter((r) => !isLongOffline(r, hideDays));
  // Client and CSA are overall filters: they scope the tiles too, not just lists.
  // Client, CSA and group-by-client are Support-and-up tools; a trader only sees their own accounts.
  const supportView = Boolean(viewer.canManage);
  const scoped = supportView
    ? all.filter((r) => (!clientSel || clientIdOf(r) === clientSel) && (!csa || (r.csa || '') === csa)
      && (!buildSel || buildKind(r) === buildSel))
    : all;
  const shownGroup = supportView ? group : 'account';
  const filters = { q: q.trim().toLowerCase(), view, tradeState };
  const live = scoped.filter((r) => matches(r, filters));
  const needs = scoped.filter(isProblem).filter((r) => matches(r, filters));
  const needsTotal = scoped.filter(isProblem).length;
  const csaNames = useMemo(() => [...new Set(everything.map((r) => r.csa).filter(Boolean))].sort(), [everything]);
  const clientOptions = useMemo(() => {
    const byKey = new Map();
    for (const r of everything) {
      const key = clientIdOf(r);
      if (!byKey.has(key)) byKey.set(key, { key, name: r.client, email: r.clientEmail });
    }
    // Two traders with the same name: add the email so the list tells them apart.
    const nameCount = new Map();
    for (const c of byKey.values()) nameCount.set(clientKey(c.name), (nameCount.get(clientKey(c.name)) || 0) + 1);
    return [...byKey.values()]
      .map((c) => ({ key: c.key, label: nameCount.get(clientKey(c.name)) > 1 && c.email ? `${c.name} (${c.email})` : c.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [everything]);

  const counts = {
    total: scoped.length,
    connected: scoped.filter((r) => r.connected === 'Yes').length,
    disconnected: scoped.filter((r) => r.connected === 'No').length,
    naked: scoped.filter((r) => r.naked === 'Yes').length,
    trading: scoped.filter((r) => r.trading === 'Yes').length,
    open: scoped.filter((r) => tradeStateOf(r) === 'open').length,
    closed: scoped.filter((r) => tradeStateOf(r) === 'closed').length,
  };
  const kpis = [
    { view: 'all', label: 'Accounts', value: counts.total, note: hiddenCount && !showHidden ? `${hiddenCount} hidden · offline ${hideDays}d+` : `reporting to ${appName()}` },
    { view: 'online', label: 'Connected', value: counts.connected, tone: 'good', note: `of ${counts.total}` },
    { view: 'offline', label: 'Disconnected', value: counts.disconnected, tone: counts.disconnected ? 'bad' : '', note: counts.disconnected ? 'need a check-in' : 'all online' },
    { view: 'naked', label: 'Naked', value: counts.naked, tone: counts.naked ? 'bad' : '', note: counts.naked ? 'no protective stop' : 'all protected' },
    {
      view: 'trading',
      label: 'Trading today',
      value: counts.trading,
      tone: 'accent',
      // Midnight to midnight ET (RiskLo keeps it past the add-on's 6 pm rollover).
      note: counts.open || counts.closed ? `${counts.open} open · ${counts.closed} closed` : 'accounts with trades since midnight ET',
    },
  ];

  const pickKpi = (next) => setView((current) => (current === next && next !== 'all' ? 'all' : next));
  const viewLabel = VIEW_OPTIONS.find((o) => o.id === view)?.label;

  return (
    <BoardActionsContext.Provider value={boardActions}>
    <SupportViewContext.Provider value={supportView}>
    <div className="vd-page tw-page">
      <div className="vd-cr-titlebar">
        <PageHeading
          title="Trade Watcher"
          badge={viewer.role === 'client' ? null : <span className="tw-admin-badge">{viewer.role === 'support' ? 'Support' : viewer.role === 'brandAdmin' ? 'Brand Admin' : 'Admin'}</span>}
          description={viewer.scope === 'own'
            ? 'Live status of your trading accounts (NinjaTrader and connected brokers): connections, naked positions and P/L.'
            : 'Live account health from the NinjaTrader add-on and connected brokers: connections, naked positions and P/L.'}
        />
      </div>

      <div className="vd-cr-toolbar tw-toolbar">
        <nav className="vd-tabs vd-cr-nav" aria-label="Trade Watcher views">
          <button type="button" className={tab === 'board' ? 'active' : ''} onClick={() => setTab('board')}>
            Board
            {board && needsTotal > 0 ? <span className="tw-tab-badge">{needsTotal}</span> : null}
          </button>
          {viewer.canManage ? (
            <button type="button" className={tab === 'csas' ? 'active' : ''} onClick={() => setTab('csas')}>
              CSAs
            </button>
          ) : null}
          <button type="button" className={tab === 'connections' ? 'active' : ''} onClick={() => setTab('connections')}>
            Connections
          </button>
          {viewer.canAdminFeed ? (
            <button type="button" className={tab === 'alerts' ? 'active' : ''} onClick={() => setTab('alerts')}>
              Alerts
              {alertCount > 0 ? <span className="tw-tab-badge" aria-label={`${alertCount} unprotected positions`}>{alertCount}</span> : null}
            </button>
          ) : null}
          {viewer.canManage || viewer.autoCloseApproved ? (
            <button type="button" className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>
              Settings
            </button>
          ) : null}
        </nav>
        <div className="tw-toolbar-actions">
          {tab === 'board' ? <AutoCloseAll rows={scoped} /> : null}
          <span className={`tw-live${error ? ' is-error' : ''}`}>
            <span className="tw-live-dot" aria-hidden="true" />
            {error || (board?.updated ? `Updated ${agoText(board.updated)}` : 'Waiting for data')}
          </span>
          {/* Same label while loading (the icon spins), so the button never changes size. */}
          <button
            type="button"
            className={`vd-btn vd-client-resync-btn tw-refresh-btn${refreshing ? ' is-refreshing' : ''}`}
            onClick={load}
            disabled={refreshing}
            aria-busy={refreshing}
          >
            <IconRefresh size={14} /> Refresh
          </button>
          {tab === 'board' ? (
            <button type="button" className="vd-btn" onClick={() => setTab('connections')}>Connect accounts</button>
          ) : null}
        </div>
      </div>

      {tab === 'board' && viewer.selfUseHidden ? (
        <p className="tw-muted tw-self-use-note" role="note">
          Showing hands-off (CSA) accounts and your own. Self-use accounts are visible to Full Admin only.
        </p>
      ) : null}

      {tab === 'csas' && viewer.canManage ? (
        <div className="tw-setup">
          {/* Support + Admin: CSA emails and who they cover. */}
          <CsaDirectory headers={twHeaders} onChanged={load} />
        </div>
      ) : tab === 'connections' ? (
        <div className="tw-setup">
          <TradeWatcherConnections
            returnInfo={connectReturn}
            addonAccounts={viewer.scope === 'own' ? (board?.live || []).filter((r) => !isBroker(r)).length : 0}
            onBoardChanged={load}
            addonSetup={viewer.canConfigureFeed
              ? <SetupView onChanged={load} canAdmin={Boolean(viewer.canAdminFeed)} canManage={Boolean(viewer.canManage)} showRecorder={false} />
              : null}
          />
        </div>
      ) : tab === 'alerts' && viewer.canAdminFeed ? (
        <div className="tw-setup">
          <React.Suspense fallback={<p className="tw-muted">Loading alerts…</p>}>
            <TradeWatchAlerts />
          </React.Suspense>
        </div>
      ) : tab === 'settings' ? (
        <div className="tw-setup">
          {/* Board settings and admin tools are Support and up; everyone else
              only gets their own add-on and feed URL. */}
          {viewer.canManage ? <ClientFeedLookup headers={twHeaders} /> : null}
          {viewer.canManage ? <BoardSettings hideDays={hideDays} onChange={chooseHideDays} /> : null}
          {viewer.canAdminFeed ? <SelfUseVisibilitySettings onChanged={load} /> : null}
          {board?.viewer?.canRestore ? <RemovedAccounts onChanged={load} /> : null}
          {viewer.autoCloseApproved ? <AutoCloseApprovalSettings approvedAt={viewer.autoCloseApprovedAt} onChanged={load} /> : null}
          {/* The add-on's install steps and feed URL live in Connections → NinjaTrader add-on. */}
          {viewer.canAdminFeed ? <SetupView onChanged={load} canAdmin showInstall={false} /> : null}
          {viewer.canAdminFeed ? <TallyTwoReleases /> : null}
        </div>
      ) : (
        <>
          <div className="tw-kpis" role="group" aria-label="Filter by status">
            {kpis.map((k) => {
              const active = view === k.view;
              return (
                <button
                  key={k.label}
                  type="button"
                  className={`tw-kpi${k.tone ? ` is-${k.tone}` : ''}${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => pickKpi(k.view)}
                  disabled={!board}
                >
                  <span className="tw-label">{k.label}</span>
                  <span className="tw-kpi-value">{board ? k.value : '—'}</span>
                  <span className="tw-muted tw-kpi-note">{board ? k.note : ''}</span>
                </button>
              );
            })}
          </div>

          {board && everything.length > 0 ? (
            <div className="tw-filters">
              <label className="tw-search">
                <IconSearch size={15} />
                <input
                  type="search"
                  placeholder={supportView ? 'Search client or account' : 'Search account'}
                  aria-label={supportView ? 'Search client or account' : 'Search account'}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </label>
              {supportView ? (
                <>
                  <select className="tw-input" aria-label="Filter by client" value={clientSel} onChange={(e) => setClientSel(e.target.value)}>
                    <option value="">All clients</option>
                    {clientOptions.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <select className="tw-input" aria-label="Filter by CSA" value={csa} onChange={(e) => setCsa(e.target.value)}>
                    <option value="">All CSAs</option>
                    {csaNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </>
              ) : null}
              {/* Phones: View (and Positions) as compact dropdowns instead of
                  six wrapping buttons. Desktop keeps the button groups. */}
              <div className={`tw-mobile-selects${view === 'trading' ? ' has-positions' : ''}`}>
                <select className="tw-input" aria-label="View" value={view} onChange={(e) => setView(e.target.value)}>
                  {VIEW_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.id === 'all' ? 'All accounts' : o.label}</option>)}
                </select>
                {view === 'trading' ? (
                  <select className="tw-input" aria-label="Open or closed positions" value={tradeState} onChange={(e) => setTradeState(e.target.value)}>
                    <option value="all">{`All positions · ${counts.trading}`}</option>
                    <option value="open">{`Open · ${counts.open}`}</option>
                    <option value="closed">{`Closed · ${counts.closed}`}</option>
                  </select>
                ) : null}
              </div>
              <div className="tw-filter-group tw-filter-group--view">
                <span className="vd-control-label">View</span>
                <div className="vd-toggle-group">
                  {VIEW_OPTIONS.filter((o) => VIEW_BUTTONS.includes(o.id)).map((o) => (
                    <button key={o.id} type="button" className={`vd-toggle ${view === o.id ? 'active' : ''}`} onClick={() => setView(o.id)}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              {supportView ? (
                <div className="tw-filter-group tw-filter-group--build">
                  <span className="vd-control-label">Build</span>
                  <div className="vd-toggle-group" role="group" aria-label="Filter by add-on build">
                    {BUILD_FILTERS.map((b) => (
                      <button key={b.id || 'all'} type="button" className={`vd-toggle ${buildSel === b.id ? 'active' : ''}`} onClick={() => setBuildSel(b.id)}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              {view === 'trading' ? (
                <div className="tw-filter-group tw-filter-group--positions">
                  <span className="vd-control-label">Positions</span>
                  <div className="vd-toggle-group" role="group" aria-label="Open or closed positions">
                    {[['all', `All ${counts.trading}`], ['open', `Open ${counts.open}`], ['closed', `Closed ${counts.closed}`]].map(([id, label]) => (
                      <button key={id} type="button" className={`vd-toggle ${tradeState === id ? 'active' : ''}`} onClick={() => setTradeState(id)}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              {supportView ? (
                <div className="tw-filter-group tw-filter-group--half">
                  <span className="vd-control-label">Group</span>
                  <div className="vd-toggle-group">
                    <button type="button" className={`vd-toggle ${group === 'account' ? 'active' : ''}`} onClick={() => chooseGroup('account')}>Account</button>
                    <button type="button" className={`vd-toggle ${group === 'client' ? 'active' : ''}`} onClick={() => chooseGroup('client')}>Client</button>
                  </div>
                </div>
              ) : null}
              <div className="tw-filter-group tw-filter-group--half">
                <span className="vd-control-label">Layout</span>
                <div className="vd-toggle-group">
                  <button type="button" className={`vd-toggle ${layout === 'cards' ? 'active' : ''}`} onClick={() => chooseLayout('cards')}>Cards</button>
                  <button type="button" className={`vd-toggle ${layout === 'list' ? 'active' : ''}`} onClick={() => chooseLayout('list')}>List</button>
                </div>
              </div>
            </div>
          ) : null}

          {viewer.canManage ? <BuildMismatchBanner items={board?.buildMismatches} /> : null}

          {hiddenCount > 0 ? (
            <div className="tw-hidden-note">
              <span>
                {showHidden
                  ? `Showing ${hiddenCount} account${hiddenCount === 1 ? '' : 's'} offline ${hideDays}+ days.`
                  : `${hiddenCount} account${hiddenCount === 1 ? '' : 's'} offline ${hideDays}+ days hidden.`}
              </span>
              <button type="button" className="tw-text-btn" onClick={() => setShowHidden((v) => !v)}>
                {showHidden ? 'Hide them' : 'Show them'}
              </button>
              {viewer.canManage ? (
                <button type="button" className="tw-link" onClick={() => setTab('settings')}>Change</button>
              ) : null}
            </div>
          ) : null}

          {approvalFor ? (
            <AutoCloseApproval
              count={approvalFor.filter(canAutoClose).length}
              onCancel={() => setApprovalFor(null)}
              onApproved={() => {
                const rows = approvalFor;
                setApprovalFor(null);
                sendAutoClose(rows, true, { approvedNow: true });
              }}
            />
          ) : null}

          {autoCloseNote ? (
            <div className="tw-hidden-note" role="status">
              <span>{autoCloseNote}</span>
              <button type="button" className="tw-link" onClick={() => setAutoCloseNote('')}>Dismiss</button>
            </div>
          ) : null}

          {removeNotice ? (
            <div className="tw-hidden-note" role="status">
              <span>{removeNotice}</span>
              <button type="button" className="tw-link" onClick={() => setTab('settings')}>Undo in Settings</button>
              <button type="button" className="tw-link" onClick={() => setRemoveNotice('')}>Dismiss</button>
            </div>
          ) : null}

          {!board && !error ? <p className="tw-empty">Loading board…</p> : null}

          {board && everything.length === 0 && viewer.scope === 'own' ? (
            <section className="vd-panel tw-onboarding">
              <div>
                <h3>No accounts linked to you yet</h3>
                <p className="tw-muted">
                  Switch on a broker you connected in My Portfolio (Alpaca, Tradovate, Schwab, Robinhood and more), or
                  connect NinjaTrader with your own feed URL, and your accounts appear here within a couple of minutes.
                  Already sending to your firm&apos;s shared URL? Ask your support team to link your name to the email you
                  sign in to {appName()} with.
                </p>
              </div>
              <button type="button" className="vd-btn" onClick={() => setTab('connections')}>Connect accounts</button>
            </section>
          ) : null}

          {board && everything.length === 0 && viewer.scope !== 'own' ? (
            <section className="vd-panel tw-onboarding">
              <div>
                <h3>No accounts reporting yet</h3>
                <p className="tw-muted">
                  Point the NinjaTrader add-on at your feed URL, or switch on broker accounts under Connections, and they appear here, live.
                </p>
              </div>
              <button type="button" className="vd-btn" onClick={() => setTab('connections')}>Connect accounts</button>
            </section>
          ) : null}

          {board && everything.length > 0 ? (
            <>
              {needs.length > 0 ? (
                <AccountsPanel
                  title="Needs attention"
                  subtitle={supportView
                    ? `${new Set(needs.map(clientIdOf)).size} clients · ${needs.length} accounts. Disconnected, naked, NinjaTrader not set to Eastern time, or the wrong add-on build. Open a client to see which accounts.`
                    : 'Disconnected, naked, or NinjaTrader not set to Eastern time.'}
                  rows={needs}
                  layout={supportView ? 'list' : layout}
                  group={supportView ? 'client' : shownGroup /* Support: one compact row per client */}
                  tone="alert"
                  showWhy
                  empty=""
                  onLogs={setLogsRow}
                />
              ) : view === 'all' || view === 'problems' ? (
                <div className="tw-all-clear">
                  <span className="tw-live-dot" aria-hidden="true" />
                  Nothing needs attention. Every matching account is connected and protected.
                </div>
              ) : null}
              <AccountsPanel
                title={view === 'all' ? 'Accounts'
                  : view === 'trading' && tradeState !== 'all' ? `Accounts · Trading · ${tradeState === 'open' ? 'Open' : 'Closed'}`
                  : `Accounts · ${viewLabel}`}
                subtitle={`${live.length} of ${scoped.length} shown`}
                rows={live}
                layout={layout}
                group={shownGroup}
                empty="No accounts match these filters."
                onLogs={setLogsRow}
              />
            </>
          ) : null}
        </>
      )}

      {logsRow ? (
        <LogsDrawer
          row={logsRow}
          onClose={() => setLogsRow(null)}
          onCsaSaved={() => { setLogsRow(null); load(); }}
          canManage={viewer.canManage}
          onLinksChanged={load}
          // Full Admin: any account. Support: hands-off (CSA) accounts only.
          canRemove={Boolean(board?.viewer?.canRemove
            || (board?.viewer?.canRemoveHandsOff && (logsRow.csa || isCsaBuild(logsRow))))}
          onOpenCsas={() => { setLogsRow(null); setTab('csas'); }}
          clientAccountCount={clientAccountCount(logsRow)}
          onRemove={(scope) => askRemove(logsRow, scope)}
        />
      ) : null}

      {removeTarget ? (
        <RemoveConfirm
          target={removeTarget}
          busy={removing}
          error={removeError}
          onCancel={() => { if (!removing) setRemoveTarget(null); }}
          onConfirm={runRemove}
        />
      ) : null}

    </div>
    </SupportViewContext.Provider>
    </BoardActionsContext.Provider>
  );
}
